/**
 * Designations View Page — read-only detail.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne } from '@refinedev/core';
import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '@yesbos/ui';
import { ArrowLeft, Edit } from 'lucide-react';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</dt>
      <dd className="text-sm font-medium">{value ?? "—"}</dd>
    </div>
  );
}

export function DesignationsViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useOne({ resource: 'designations', id: id! });
  const des = data?.data as Record<string, unknown> | undefined;

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;
  if (!des) return <div className="p-6 text-sm text-destructive">Designation not found.</div>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/designations')}><ArrowLeft className="h-4 w-4" /></Button>
          <h1 className="text-2xl font-semibold">{String(des.title ?? '')}</h1>
          <Badge variant={des.is_active ? 'default' : 'secondary'}>{des.is_active ? 'Active' : 'Inactive'}</Badge>
        </div>
        <Button variant="outline" onClick={() => navigate(`/designations/${id}/edit`)}>
          <Edit className="h-4 w-4 mr-2" />Edit
        </Button>
      </div>
      <Card>
        <CardHeader><CardTitle>Designation Details</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Title" value={String(des.title ?? '')} />
            <Field label="Grade" value={String(des.grade ?? '')} />
            <Field label="Level" value={String(des.level ?? '')} />
            <Field label="Min Salary" value={des.min_salary ? `₹${des.min_salary}` : undefined} />
            <Field label="Max Salary" value={des.max_salary ? `₹${des.max_salary}` : undefined} />
          </dl>
          {des.description && (
            <div className="mt-4 space-y-1">
              <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Description</dt>
              <dd className="text-sm">{String(des.description)}</dd>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default DesignationsViewPage;
